/**
 * <Qr> — un código QR dibujado como SVG, sin canvas y sin red.
 *
 * La matriz la calcula `qrcode-generator` (MIT, sin dependencias): la parte
 * difícil del QR es Reed-Solomon y el enmascarado, y un QR casero sin
 * verificación de escaneo es peor que un link legible. Lo que hacemos acá es
 * sólo dibujar: un único `<path>` con un cuadrado por módulo oscuro, que pesa
 * poco, escala sin perder nitidez y se imprime bien.
 *
 * Corrección de errores `M` (15 %): alcanza para una pantalla de laptop con
 * reflejos y deja el código chico; la URL de la consola entra cómoda.
 */
import { useMemo } from 'react';
import qrcode from 'qrcode-generator';

export interface QrMatrix {
  size: number;
  /** Camino SVG con un cuadrado de 1×1 por módulo oscuro. */
  path: string;
}

/** Texto → matriz QR (versión automática). Puro: lo usan el componente y los tests. */
export function qrMatrix(text: string, level: 'L' | 'M' | 'Q' | 'H' = 'M'): QrMatrix {
  const qr = qrcode(0, level);
  qr.addData(text, 'Byte');
  qr.make();
  const size = qr.getModuleCount();
  let path = '';
  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) {
      if (qr.isDark(row, col)) path += `M${col} ${row}h1v1h-1z`;
    }
  }
  return { size, path };
}

export function Qr({
  value,
  size = 200,
  label,
}: {
  value: string;
  /** Lado en px. */
  size?: number;
  /** Rótulo accesible. */
  label: string;
}): JSX.Element {
  const matrix = useMemo(() => qrMatrix(value), [value]);
  // Zona de silencio de 2 módulos (el estándar pide 4; el borde blanco de la
  // tarjeta completa el resto).
  const quiet = 2;
  const box = matrix.size + quiet * 2;
  return (
    <svg
      className="qr"
      viewBox={`${-quiet} ${-quiet} ${box} ${box}`}
      width={size}
      height={size}
      role="img"
      aria-label={label}
      shapeRendering="crispEdges"
    >
      <title>{label}</title>
      <rect x={-quiet} y={-quiet} width={box} height={box} fill="#fff" />
      <path d={matrix.path} fill="#171b19" />
    </svg>
  );
}
